import { useState, useMemo, useEffect } from "react";
import { __ } from "services/Translator";
import { useWithdrawEngine, buildWithdrawMethods } from "./useCashierData";
import { useWithdrawExtras } from "./useWithdrawExtras";
import InfoCard from "./InfoCard";
import MethodTile from "./MethodTile";
import AmountField from "./AmountField";
import { WithdrawIcoIcon, WithdrawCtaIcon, ClockSmallIcon, KeypadIcon, GoBackIcon, ChevronRightIcon } from "./icons";

// On-brand editable text input for payout extras (2FA code, wallet address,
// tag, PagSmile-style fields). Reuses the SCSS custom properties so it matches
// the dark cashier surface without adding new stylesheet rules.
const EXTRA_INPUT_STYLE = {
  width: "100%",
  fontSize: "14px",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid var(--stroke)",
  background: "var(--glass-2)",
  color: "var(--ink)",
  outline: "none",
};

const round2 = (n) => Math.round(n * 100) / 100;

export default function WithdrawView({ cashier = {} }) {
  const engine = useWithdrawEngine();
  const currency = cashier.currency || "EUR";
  const formatMoney = useMemo(() => cashier.formatMoney || ((v) => String(v)), [cashier.formatMoney]);
  const profile = cashier.profile || {};

  // Only engine-provided methods carry a method_id (required to submit). No
  // static fallback: a fallback tile without method_id would be permanently
  // unsubmittable. Empty methods render an honest empty state instead.
  const methods = useMemo(
    () => buildWithdrawMethods(engine.methods, currency),
    [engine.methods, currency]
  );

  const [method, setMethod] = useState(methods[0]);
  // Two-step flow (mirrors Deposit): 1 = pick payout method, 2 = amount + details.
  // Keeps each step short so the payout form no longer needs a long scroll.
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaSent, setTwoFaSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState(null);

  // Keep a valid selected method as the engine data arrives.
  useEffect(() => {
    if (!methods.length) return;
    if (!method || !methods.find((m) => m.name === method.name)) setMethod(methods[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods]);

  // Raw engine row for the selected method: it carries method_id (crypto/bank
  // detection) and, for PagSmile-style methods, the `fields` array. The tile
  // shape does not, so look it up for the payout-extras hook.
  const rawMethod = useMemo(
    () => (method ? (engine.methods || []).find((m) => m.method_id === method.method_id) : null),
    [engine.methods, method]
  );
  const extras = useWithdrawExtras(rawMethod);

  const details = engine.details || {};
  // F6: single withdrawable source = engine details.total_withdrawal_amount (the
  // card InfoCard, the "Available" line and the max validation all read this).
  const withdrawable = Number(details.total_withdrawal_amount);
  const hasWithdrawable = Number.isFinite(withdrawable);
  const minWithdrawal = method && method.minWithdrawal ? Number(method.minWithdrawal) : 0;

  // F5: the available balance carries cents, so the amount accepts up to two
  // decimals; the enforced bound then equals the displayed "up to X".
  const numeric = useMemo(() => {
    const v = parseFloat(String(amount));
    return Number.isFinite(v) ? v : 0;
  }, [amount]);

  const validationError = useMemo(() => {
    if (numeric <= 0) return null;
    if (minWithdrawal > 0 && numeric < minWithdrawal) {
      return __("Minimum withdrawal is") + " " + formatMoney(minWithdrawal);
    }
    if (hasWithdrawable && numeric > withdrawable) {
      return __("You can withdraw up to") + " " + formatMoney(withdrawable);
    }
    return null;
  }, [numeric, minWithdrawal, hasWithdrawable, withdrawable, formatMoney]);

  // F9: any edit clears the stale success/error message.
  const clearMessage = () => setMessage(null);

  const handleAmountChange = (raw) => {
    // Digits + at most one dot + up to 2 decimals. Over-balance is surfaced by
    // the "You can withdraw up to X" message, not silently clamped.
    let s = String(raw).replace(/[^0-9.]/g, "");
    const dot = s.indexOf(".");
    if (dot !== -1) {
      const intPart = s.slice(0, dot);
      const decPart = s.slice(dot + 1).replace(/\./g, "").slice(0, 2);
      s = intPart + "." + decPart;
    }
    setAmount(s);
    clearMessage();
  };

  const selectMethod = (m) => {
    setMethod(m);
    clearMessage();
  };

  // F1: quick-amount chips (percentages of the withdrawable + Max) so a value is
  // set with one tap, and the custom field always has an obvious entry point.
  const chips = useMemo(() => {
    if (!hasWithdrawable || withdrawable <= 0) return [];
    const out = [];
    [0.25, 0.5, 0.75].forEach((p) => {
      const v = Math.floor(withdrawable * p);
      if (v > 0) out.push({ label: Math.round(p * 100) + "%", value: String(v) });
    });
    out.push({ label: __("Max"), value: String(round2(withdrawable)) });
    return out;
  }, [hasWithdrawable, withdrawable]);

  const focusAmount = () => {
    const el = document.getElementById("gcc-withdraw-amt");
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleResendTwoFa = async () => {
    const ok = await engine.resendTwoFa();
    setTwoFaSent(ok);
    if (!ok) setMessage(__("Could not send a 2FA code. Please try again."));
  };

  const handleCancelPending = async () => {
    if (cancelling) return;
    setCancelling(true);
    const ok = await engine.cancelPending();
    setCancelling(false);
    setMessage(
      ok
        ? __("Your pending withdrawal has been cancelled and the funds returned to your balance.")
        : __("Could not cancel the pending withdrawal. Please try again.")
    );
  };

  const twoFaOk = !profile.twoFaEnabled || String(twoFaCode).trim().length === 6;

  const handleSubmit = async () => {
    // MF-1: guard re-entry + disable the CTA the instant submit fires.
    if (submitting) return;
    setMessage(null);
    if (!profile.emailVerified) {
      setMessage(__("Please verify your e-mail address before requesting a withdrawal."));
      return;
    }
    if (!method || !method.method_id) {
      setMessage(__("Please select a payout method."));
      return;
    }
    if (!numeric) {
      setMessage(__("Please enter a valid amount for withdrawal"));
      return;
    }
    if (extras.addressRequired && !String(extras.address).trim()) {
      setMessage(__("Please enter your wallet address to continue."));
      return;
    }
    if (!extras.extrasValid) {
      setMessage(__("Please complete the required payout details."));
      return;
    }
    if (profile.twoFaEnabled && String(twoFaCode).trim().length !== 6) {
      setMessage(__("Please enter the 6-digit 2FA code sent to your email."));
      return;
    }
    setSubmitting(true);
    try {
      const res = await engine.submitWithdraw({
        amount: numeric,
        method: method.method_id,
        extraFields: extras.extraFields,
        accNumber: extras.addressRequired ? String(extras.address).trim() : undefined,
        tag: extras.tagApplicable && String(extras.tag).trim() ? String(extras.tag).trim() : undefined,
        twoFaCode: profile.twoFaEnabled ? String(twoFaCode).trim() : undefined,
      });
      if (res && res.status === 200) {
        setMessage(__("Your withdrawal request has been received"));
        setAmount("");
        setTwoFaCode("");
        setTwoFaSent(false);
      } else {
        // KYC / hold / 2FA / min are enforced server-side: surface its message.
        setMessage(
          res && res.data && res.data.message ? __(res.data.message) : __("Your withdrawal could not be completed.")
        );
      }
    } catch (e) {
      setMessage(__("Your withdrawal could not be completed."));
    }
    setSubmitting(false);
  };

  const withdrawableLabel = hasWithdrawable ? formatMoney(withdrawable) : "—";
  const hasMethods = methods.length > 0;
  const pendingAmount = engine.pending && Number(engine.pending.amount) > 0 ? Number(engine.pending.amount) : 0;

  return (
    <div className="wd">
      {step === 1 ? (
        <>
      <InfoCard gold style={{ marginBottom: "18px" }}>
        <div className="ic-top">
          <span className="ic-ico em">
            <WithdrawIcoIcon />
          </span>
          <div className="tt">
            <b>{__("Withdrawable balance")}</b>
            <small>{__("Bonus funds are excluded until wagered")}</small>
          </div>
          <span className="amt">{withdrawableLabel}</span>
        </div>
      </InfoCard>

      {pendingAmount > 0 ? (
        <InfoCard style={{ marginBottom: "18px" }}>
          <div className="ic-top">
            <span className="ic-ico">
              <ClockSmallIcon />
            </span>
            <div className="tt">
              <b>{__("Pending withdrawal")}</b>
              <small>{__("Cancel to return the funds to your balance")}</small>
            </div>
            <span className="amt">{formatMoney(pendingAmount)}</span>
          </div>
          <button
            type="button"
            onClick={handleCancelPending}
            disabled={cancelling}
            style={{ ...EXTRA_INPUT_STYLE, cursor: cancelling ? "default" : "pointer", marginTop: "12px", fontWeight: 700 }}
          >
            {cancelling ? __("Cancelling...") : __("Cancel pending withdrawal")}
          </button>
        </InfoCard>
      ) : null}

      <div className="field-lbl">{__("Withdraw to")}</div>
      {engine.withdrawLoading ? (
        <div className="methods" style={{ marginBottom: "20px" }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="tile skel-tile" aria-hidden="true" />
          ))}
        </div>
      ) : hasMethods ? (
        <div className="methods" style={{ marginBottom: "20px" }}>
          {methods.map((m) => (
            <MethodTile key={m.name} method={m} selected={method && method.name === m.name} onSelect={selectMethod} />
          ))}
        </div>
      ) : (
        <div className="note-line" style={{ marginBottom: "20px" }}>
          {__("No payout methods are available right now. Please contact support.")}
        </div>
      )}

          {!engine.withdrawLoading && hasMethods ? (
            <button
              className="cta"
              style={{ marginTop: "18px" }}
              disabled={!method || !method.method_id}
              onClick={() => setStep(2)}
            >
              <span style={{ whiteSpace: "nowrap" }}>{__("Continue")}</span>
              <ChevronRightIcon />
            </button>
          ) : null}

          {(engine.withdrawLoading || !hasMethods) && message ? (
            <div className="under-cta" style={{ marginTop: "6px" }}>
              {message}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div
            className="goback"
            onClick={() => setStep(1)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setStep(1);
              }
            }}
          >
            <GoBackIcon />
            {__("Go back")}
          </div>

          {chips.length ? (
            <div className="presets">
              {chips.map((c) => (
                <div
                  key={c.label}
                  className={numeric === Number(c.value) ? "chip sel" : "chip"}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleAmountChange(c.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAmountChange(c.value);
                    }
                  }}
                >
                  {c.label}
                </div>
              ))}
            </div>
          ) : null}

          <AmountField
            label={__("Withdrawal amount")}
            symbol={cashier.currencySymbol || "€"}
            value={amount}
            onChange={handleAmountChange}
            maxLength={10}
            inputId="gcc-withdraw-amt"
            limits={
              <>
                {minWithdrawal > 0 ? (
                  <>
                    {__("Min")} <b>{formatMoney(minWithdrawal)}</b> &nbsp;·&nbsp;{" "}
                  </>
                ) : null}
                {__("Available")} <b>{withdrawableLabel}</b>
              </>
            }
          />

          <div className="keypad-hint" onClick={focusAmount}>
            <span>
              {__("Prefer a custom amount?")} <b>{__("Tap to type")}</b>
            </span>
            <KeypadIcon />
          </div>

          {/* F3: crypto / wallet-address payout details. The address is sent as
              the top-level accNumber and the tag as top-level tag, exactly as the
              backend withdraw() reads them (CoinsPaid/AlphaPo payout). */}
          {extras.addressRequired ? (
            <div className="amount-wrap crypto-addr" style={{ marginBottom: "18px" }}>
              <div className="lab">{__("Wallet address")}</div>
              <input
                className="val"
                value={extras.address}
                onChange={(e) => {
                  extras.setAddress(e.target.value);
                  clearMessage();
                }}
                placeholder={__("Enter your destination wallet address")}
                autoComplete="off"
                spellCheck={false}
                style={EXTRA_INPUT_STYLE}
              />
              {extras.tagApplicable ? (
                <>
                  <div className="lab" style={{ marginTop: "14px" }}>
                    {__("Tag / Memo")}
                  </div>
                  <input
                    className="val"
                    value={extras.tag}
                    onChange={(e) => {
                      extras.setTag(e.target.value);
                      clearMessage();
                    }}
                    placeholder={__("Only for coins that require a tag/memo (XRP, XLM, ...)")}
                    autoComplete="off"
                    spellCheck={false}
                    style={EXTRA_INPUT_STYLE}
                  />
                </>
              ) : null}
              <div className="limits">{__("Double-check the address. Payouts sent to a wrong address cannot be recovered.")}</div>
            </div>
          ) : null}

          {/* PagSmile-style method-driven fields (mirrors CashierModals: the
              method exposes a `fields` array collected into extraFields). */}
          {extras.fields.length ? (
            <div className="amount-wrap crypto-addr" style={{ marginBottom: "18px" }}>
              {extras.fields.map((field, i) => (
                <div key={field.name || i} style={{ marginTop: i ? "14px" : 0 }}>
                  <div className="lab">{field.label || field.name}</div>
                  {field.type === "select" ? (
                    <select
                      className="val"
                      value={extras.extraFields[field.name] || ""}
                      onChange={(e) => {
                        extras.setExtraField(field.name, e.target.value);
                        clearMessage();
                      }}
                      style={EXTRA_INPUT_STYLE}
                    >
                      <option value="">{__("Select")}</option>
                      {Object.entries(field.values || {}).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="val"
                      value={extras.extraFields[field.name] || ""}
                      onChange={(e) => {
                        extras.setExtraField(field.name, e.target.value);
                        clearMessage();
                      }}
                      autoComplete="off"
                      style={EXTRA_INPUT_STYLE}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {/* F2: 2FA code input, shown only when the player has 2FA enabled
              (profile.twoFaEnabled). Non-2FA players are never blocked. */}
          {profile.twoFaEnabled ? (
            <div className="amount-wrap crypto-addr" style={{ marginBottom: "18px" }}>
              <div className="lab">{__("2FA code")}</div>
              <input
                className="val"
                value={twoFaCode}
                onChange={(e) => {
                  setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  clearMessage();
                }}
                inputMode="numeric"
                placeholder={__("Enter the 6-digit code sent to your email")}
                autoComplete="one-time-code"
                style={EXTRA_INPUT_STYLE}
              />
              <div className="crypto-status">
                <button type="button" className="retry" onClick={handleResendTwoFa}>
                  {__("Get 2FA code")}
                </button>
                {twoFaSent ? <span>{__("A new code has been sent to your email.")}</span> : null}
              </div>
            </div>
          ) : null}

          {validationError ? <div className="val-msg">{validationError}</div> : null}

          {message ? (
            <div className="under-cta" style={{ marginBottom: "6px" }}>
              {message}
            </div>
          ) : null}

          <button
            className="cta"
            onClick={handleSubmit}
            disabled={
              submitting ||
              numeric <= 0 ||
              !!validationError ||
              !method ||
              !method.method_id ||
              !extras.extrasValid ||
              !twoFaOk
            }
          >
            <WithdrawCtaIcon />
            <span>{submitting ? __("Processing...") : __("Withdraw") + " " + formatMoney(numeric)}</span>
          </button>

          <div className="under-cta">
            <ClockSmallIcon />
            {__("Most withdrawals are reviewed within 8 hours")}
          </div>
        </>
      )}
    </div>
  );
}
